import React, { useState, useEffect } from 'react'
import { Users, X, Pencil } from 'lucide-react'
import { PlusIcon } from '@heroicons/react/24/outline'
import { MotionButton } from '../../components/ui/motion-button'
import { useUsers } from '../../hooks/users'
import { toast } from '../../components/ui/use-toast'
import { User, Branch, BranchList, CategorizedUsers } from '@/types/ManageUsers'
import { validateEmail } from '@/lib/helpers'
import EditUserForm from '../../components/UserForm/EditUserForm'
import NewUserForm from '../../components/UserForm/NewUserForm'
import CurvedTriangle from '@/images/customSvg/buttonCurve'
import { useOutletContext } from 'react-router-dom'

const ManageUsers: React.FC = () => {
  // Safe destructuring with fallback
  const outletContext = useOutletContext<{ setShowAlert?: (show: boolean) => void }>()
  const setShowAlert = outletContext?.setShowAlert || (() => { })

  const { loadUsers, addUser, updateUser, loadBranches } = useUsers()
  const [categorizedUsers, setCategorizedUsers] = useState<CategorizedUsers>({
    shopOwners: [],
    shopManagers: [],
    regularUsers: [],
  })
  const [branchData, setBranchData] = useState<BranchList | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [newUser, setNewUser] = useState<User | null>(null)
  const [history, setHistory] = useState<Branch[]>([])
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false)
  const [loadingUsers, setLoadingUsers] = useState<boolean>(false)
  const [loadingBranches, setLoadingBranches] = useState<boolean>(false)
  const [loadingModal, setLoadingModal] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [newUserEmailError, setNewUserEmailError] = useState<string | null>(
    null
  )
  const [showEditModal, setShowEditModal] = useState(false)
  const [showNewUserModal, setShowNewUserModal] = useState(false)
  const [activeTab, setActiveTab] = useState<
    'shopOwners' | 'shopManagers' | 'regularUsers'
  >('shopOwners')

  const primaryColor = `hsl(${getComputedStyle(document.documentElement)
    .getPropertyValue('--primary')
    .trim()})`

  const secondaryColor = `hsl(${getComputedStyle(document.documentElement)
    .getPropertyValue('--secondary')
    .trim()})`

  useEffect(() => {
    setLoadingBranches(true)
    loadBranches().then((data) => {
      setBranchData(data)
      setSelectedBranch(data?.branch || null)
      loadInitialUsers()
      if (data?.branch) fetchUsers(data.branch.id)
      setLoadingBranches(false)
    })
  }, [])

  const loadInitialUsers = async () => {
    setLoadingUsers(true)
    const users = await loadUsers()
    organizeUsers(users)
    setLoadingUsers(false)
  }

  const fetchUsers = async (branchId: string) => {
    setLoadingUsers(true)
    setCategorizedUsers({
      shopOwners: [],
      shopManagers: [],
      regularUsers: [],
    })
    const branchUsers = await loadUsers(branchId)
    organizeUsers(branchUsers)
    setLoadingUsers(false)
    setInitialLoadComplete(true)
  }

  const organizeUsers = (users: User[]) => {
    const shopOwners = users.filter(
      (user) =>
        user.user_level === 'power_user' && user.user_profile === 'shop_owner'
    )
    const shopManagers = users.filter(
      (user) =>
        user.user_level === 'power_user' && user.user_profile === 'shop_manager'
    )
    const regularUsers = users.filter((user) => user.user_level === 'user')

    setCategorizedUsers({
      shopOwners,
      shopManagers,
      regularUsers,
    })
  }

  const handleBranchSelect = (branch: Branch) => {
    if (selectedBranch) {
      setHistory((prevHistory) => [...prevHistory, selectedBranch])
    }

    setLoadingUsers(true)
    setLoadingBranches(true)
    setSelectedUser(null)
    setNewUser(null)
    setShowEditModal(false)
    setShowNewUserModal(false)

    loadBranches(branch.id).then((data) => {
      setBranchData(data)
      setSelectedBranch(branch)
      fetchUsers(branch.id).finally(() => {
        setLoadingBranches(false)
        setLoadingUsers(false)
      })
    })
  }

  useEffect(() => {
    if (branchData && selectedBranch) {
      setSelectedBranch(branchData.branch)
    }
  }, [branchData])

  const handleBack = async () => {
    if (history.length > 0) {
      const previousBranch = history[history.length - 1]
      setHistory((prevHistory) => prevHistory.slice(0, -1))
      setSelectedBranch(previousBranch)
      setSelectedUser(null)
      setNewUser(null)
      setShowEditModal(false)
      setShowNewUserModal(false)

      setLoadingUsers(true)
      setLoadingBranches(true)

      await loadBranches(previousBranch.id).then((data) => {
        setBranchData(data)
      })

      await fetchUsers(previousBranch.id)

      setLoadingBranches(false)
      setLoadingUsers(false)
    }
  }

  const handleUserClick = (user: User) => {
    setSelectedUser(user)
    setNewUser(null)
    setShowEditModal(true)
    setShowNewUserModal(false)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      const { name, value } = e.target
      if (name === 'email') {
        if (!validateEmail(value)) {
          setEmailError('Invalid email format')
        } else {
          setEmailError(null)
        }
      }
      setSelectedUser({ ...selectedUser, [name]: value })
    }
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (selectedUser) {
      const { name, value } = e.target
      setSelectedUser({ ...selectedUser, [name]: value })
    }
  }

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedUser) {
      setSelectedUser({ ...selectedUser, active: e.target.checked })
    }
  }

  const handleCancel = () => {
    setSelectedUser(null)
    setNewUser(null)
    setShowEditModal(false)
    setShowNewUserModal(false)
    setEmailError(null)
    setNewUserEmailError(null)
  }

  const handleSave = async () => {
    if (selectedUser) {
      setLoadingModal(true)
      try {
        const updatedUser = await updateUser(selectedUser)

        if (updatedUser) {

          setLoadingModal(false)

          setCategorizedUsers((prev) => {
            const { shopOwners, shopManagers, regularUsers } = prev

            const updatedShopOwners = shopOwners.map((user) =>
              user.id === updatedUser.id ? updatedUser : user
            )
            const updatedShopManagers = shopManagers.map((user) =>
              user.id === updatedUser.id ? updatedUser : user
            )
            const updatedRegularUsers = regularUsers.map((user) =>
              user.id === updatedUser.id ? updatedUser : user
            )

            return {
              shopOwners: updatedShopOwners,
              shopManagers: updatedShopManagers,
              regularUsers: updatedRegularUsers,
            }
          })
          toast({
            title: 'User information saved.',
            description: 'User information saved.',
          })
          if (selectedBranch) {
            await fetchUsers(selectedBranch.id)
          }
          handleCancel()
        }
      } catch (error) {
        setShowAlert(true)
        setLoadingModal(false)
        console.error('Failed to update user:', error)
        toast({
          title: 'Uh Oh ... the user update failed.',
          description: 'If the problem persists contact a representative.',
          variant: 'destructive',
        })
      } finally {
        setLoadingModal(false)
      }
    }
  }

  const handleNewUserInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (newUser) {
      const { name, value } = e.target
      if (name === 'email') {
        if (!validateEmail(value)) {
          setNewUserEmailError('Invalid email format')
        } else {
          setNewUserEmailError(null)
        }
      }
      setNewUser({ ...newUser, [name]: value })
    }
  }

  const handleNewUserSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    if (newUser) {
      const { name, value } = e.target
      setNewUser({ ...newUser, [name]: value })
    }
  }

  const handleNewUserCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (newUser) {
      setNewUser({ ...newUser, active: e.target.checked })
    }
  }

  const handleNewUserClick = () => {
    setSelectedUser(null)
    setNewUser({
      id: '',
      first_name: '',
      last_name: '',
      email: '',
      user_level: 'user',
      user_profile: '',
      active: false,
      uuid: '',
      username: '',
    })
    setShowNewUserModal(true)
    setShowEditModal(false)
  }

  const handleAddUser = async () => {
    if (
      newUser &&
      newUser.first_name &&
      newUser.last_name &&
      newUser.email &&
      newUser.user_level
    ) {
      setLoadingModal(true)

      try {
        const userWithBranch = {
          ...newUser,
          branch_id: selectedBranch?.id
        }
        const addedUser = await addUser(userWithBranch)

        if (addedUser) {
          handleCancel();
          setLoadingModal(false)
          setCategorizedUsers((prevCategorizedUsers) => {
            const { shopOwners, shopManagers, regularUsers } =
              prevCategorizedUsers

            if (
              addedUser.user_level === 'power_user' &&
              addedUser.user_profile === 'shop_owner'
            ) {
              return {
                ...prevCategorizedUsers,
                shopOwners: [...shopOwners, addedUser],
              }
            } else if (
              addedUser.user_level === 'power_user' &&
              addedUser.user_profile === 'shop_manager'
            ) {
              return {
                ...prevCategorizedUsers,
                shopManagers: [...shopManagers, addedUser],
              }
            } else if (addedUser.user_level === 'user') {
              return {
                ...prevCategorizedUsers,
                regularUsers: [...regularUsers, addedUser],
              }
            }

            return prevCategorizedUsers
          })
          toast({
            title: 'New user added successfully.',
          })
          if (selectedBranch) {
            await fetchUsers(selectedBranch.id)
          }
          handleCancel()
        }
      } catch (error) {
        console.error('Error adding user:', error.message)
        setShowAlert(true)
        setLoadingModal(false)

        toast({
          title: 'Uh Oh ... we could not add user.',
          description: 'If the problem persists contact a representative.',
          variant: 'destructive',
        })
      } finally {
        setLoadingModal(false)
      }
    }
  }

  const mockShopOwners = Array.from({ length: 20 }).map((_, i) => ({
    id: `mock-${i}`,
    first_name: `Owner${i + 1}`,
    last_name: `Test`,
  }))

  return (
    <div className="flex flex-col lg:flex-row lg:container w-full gap-4 items-stretch">
      <div className="w-full lg:w-2/6">
        <div className="p-4 rounded border-[2px] border-gray-300 bg-white h-full">
          <div className="flex justify-between items-start mb-4">
            <h2 className="font-semibold text-gray-900 text-lg">
              Branch Navigation
            </h2>
            {history.length > 0 && (
              <button
                onClick={handleBack}
                className="text-primary hover:underline cursor-pointer transition-colors font-medium"
              >
                ← Back
              </button>
            )}
          </div>

          {loadingBranches ? (
            <div className="flex flex-col items-center justify-center w-full h-[300px]">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent mb-4"></div>
              <p className="text-lg font-semibold text-gray-800 animate-pulse">
                Loading branches...
              </p>
            </div>
          ) : branchData ? (
            <>
              <button
                onClick={() => handleBranchSelect(branchData.branch)}
                className={`p-1.5 px-3  text-left w-full transition-all duration-200 border-[2px] border-primary rounded font-medium ${selectedBranch?.id === branchData.branch.id
                  ? 'bg-primary text-white shadow-md'
                  : 'hover:bg-gray-100'
                  }`}
              >
                {branchData.branch.name}
              </button>
              <div className="mt-2 pl-4 flex flex-col gap-1 overflow-auto max-h-[450px]">
                {branchData.children.map((child) => (
                  <button
                    key={child.id}
                    onClick={() => handleBranchSelect(child)}
                    className={`p-1.5 px-3  text-left font-normal w-full rounded border-[2px] border-primary transition-all duration-200 ${selectedBranch?.id === child.id
                      ? 'bg-primary text-white shadow-md'
                      : 'hover:bg-gray-100'
                      }`}
                  >
                    {child.name}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <p className="text-gray-500">No branch data available.</p>
          )}
        </div>
      </div>

      <div className="w-full flex flex-col gap-4">
        <div className="overflow-hidden">
          <div className="flex items-end w-full">
            <button
              onClick={() => setActiveTab('shopOwners')}
              className={`
                px-6 py-3 font-semibold rounded-t text-white transition-all duration-200
                ${activeTab === 'shopOwners'
                  ? 'bg-primary -mb-px border-b-[0.5px] border-primary'
                  : 'bg-secondary border-b-[0.5px] border-gray-400'
                }
              `}
            >
              <div className="flex items-center">
                Shop Owners
                {categorizedUsers.shopOwners.length > 0 && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold
                    ${activeTab === 'shopOwners' ? 'bg-secondary' : 'bg-primary'
                      }`}
                  >
                    {categorizedUsers.shopOwners.length}
                  </span>
                )}
              </div>
            </button>

            <div
              className={`relative bottom-[0px] -ml-[3px] 
              ${activeTab === 'shopOwners'
                  ? 'border-b-[0.5px] border-primary'
                  : 'border-b-[0.5px] border-gray-400'
                }`}
            >
              <CurvedTriangle
                direction="right"
                color={
                  activeTab === 'shopOwners' ? primaryColor : secondaryColor
                }
                width={10}
                height={20}
              />
            </div>

            <div
              className={`relative bottom-[0px] -mr-[3px]
              ${activeTab === 'shopManagers'
                  ? 'border-b-[0.5px] border-primary'
                  : 'border-b-[0.5px] border-gray-400'
                }`}
            >
              <CurvedTriangle
                direction="left"
                color={
                  activeTab === 'shopManagers' ? primaryColor : secondaryColor
                }
                width={10}
                height={20}
              />
            </div>
            <button
              onClick={() => setActiveTab('shopManagers')}
              className={`
                px-6 py-3 font-semibold rounded-t text-white transition-all duration-200
                ${activeTab === 'shopManagers'
                  ? 'bg-primary -mb-px border-b-[0.5px] border-primary'
                  : 'bg-secondary border-b-[0.5px] border-gray-400'
                }
              `}
            >
              <div className="flex items-center">
                Shop Managers
                {categorizedUsers.shopManagers.length > 0 && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold
                    ${activeTab === 'shopManagers'
                        ? 'bg-secondary'
                        : 'bg-primary'
                      }`}
                  >
                    {categorizedUsers.shopManagers.length}
                  </span>
                )}
              </div>
            </button>
            <div
              className={`relative bottom-[0px] -ml-[3px] 
            ${activeTab === 'shopManagers'
                  ? 'border-b-[0.5px] border-primary'
                  : 'border-b-[0.5px] border-gray-400'
                }`}
            >
              <CurvedTriangle
                direction="right"
                color={
                  activeTab === 'shopManagers' ? primaryColor : secondaryColor
                }
                width={10}
                height={20}
              />
            </div>

            <div
              className={`relative bottom-[0px] -mr-[3px] 
            ${activeTab === 'regularUsers'
                  ? 'border-b-[0.5px] border-primary'
                  : 'border-b-[0.5px] border-gray-400'
                }`}
            >
              <CurvedTriangle
                direction="left"
                color={
                  activeTab === 'regularUsers' ? primaryColor : secondaryColor
                }
                width={10}
                height={20}
              />
            </div>

            <button
              onClick={() => setActiveTab('regularUsers')}
              className={`
                px-6 py-3 font-semibold rounded-t text-white transition-all duration-200
                ${activeTab === 'regularUsers'
                  ? 'bg-primary -mb-px border-b-[0.5px] border-primary'
                  : 'bg-secondary border-b-[0.5px] border-gray-400'
                }
              `}
            >
              <div className="flex items-center">
                Users
                {categorizedUsers.regularUsers.length > 0 && (
                  <span
                    className={`ml-2 rounded-full px-2 py-0.5 text-xs font-bold
                ${activeTab === 'regularUsers' ? 'bg-secondary' : 'bg-primary'}
                `}
                  >
                    {categorizedUsers.regularUsers.length}
                  </span>
                )}
              </div>
            </button>

            <div
              className={`relative bottom-[0px] -ml-[3px] 
              ${activeTab === 'regularUsers'
                  ? 'border-b-[0.5px] border-primary'
                  : 'border-b-[0.5px] border-gray-400'
                }`}
            >
              <CurvedTriangle
                direction="right"
                color={
                  activeTab === 'regularUsers' ? primaryColor : secondaryColor
                }
                width={10}
                height={20}
              />
            </div>
          </div>

          <div
            className={`min-h-[550px] border-4 rounded rounded-tl-none p-0 overflow-hidden transition-all
                ${activeTab === 'shopOwners' ||
                activeTab === 'shopManagers' ||
                activeTab === 'regularUsers'
                ? 'border-primary bg-primary'
                : 'border-gray-400 bg-secondary'
              }`}
          >
            <div className="bg-gradient-to-r from-primary to-primary/70 text-white p-2 shadow-md">
              <div className="flex items-center justify-between">
                {loadingBranches ? (
                  <h3 className="text-xl font-bold pl-2 flex items-center">
                    <span className="ml-1 animate-pulse">
                      Loading branches...
                    </span>
                  </h3>
                ) : (
                  <h3 className="text-xl font-bold pl-2">
                    {selectedBranch?.name || 'Select a Branch'}
                  </h3>
                )}
                <MotionButton
                  type="button"
                  onClick={handleNewUserClick}
                  className="px-2 py-1 bg-white/20 backdrop-blur-md text-white border border-white/40
                                rounded text-xs font-medium hover:bg-white hover:text-primary
                                flex items-center gap-1 transition-all"
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                  Add User
                </MotionButton>
              </div>
            </div>

            <div className="flex-1 bg-gray-100 border-2 border-black p-4">
              {loadingUsers ? (
                <div className="flex flex-col items-center justify-center h-[450px]">
                  <div className="animate-spin rounded-full h-14 w-14 border-4 border-primary border-t-transparent mb-4"></div>
                  <p className="text-lg font-semibold text-gray-800 animate-pulse">
                    Loading users...
                  </p>
                </div>
              ) : (
                <>
                  {activeTab === 'shopOwners' && (
                    <div className="animate-in fade-in duration-300 h-[450px] rounded">
                      {initialLoadComplete &&
                        categorizedUsers.shopOwners.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[450px] text-center">
                          <Users className="w-20 h-20 text-gray-300 mb-2" />
                          <p className="text-gray-500 text-xl font-medium">
                            No Shop Owners available
                          </p>
                          <p className="text-gray-400 text-sm mt-2">
                            Shop owners will appear here when added
                          </p>
                        </div>
                      ) : (
                        <div className="h-[450px] overflow-y-auto pr-1 rounded">
                          <ul className="space-y-1">
                            {categorizedUsers.shopOwners.map((user) => (
                              <li key={user.id}>
                                <div className="w-full p-1 px-2 rounded bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-200 shadow-sm">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-bold text-lg">
                                        {user.first_name.charAt(0)}
                                        {user.last_name.charAt(0)}
                                      </div>
                                      <span className="font-semibold text-gray-900 text-lg">
                                        {user.first_name} {user.last_name}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'shopManagers' && (
                    <div className="animate-in fade-in duration-300 h-[450px]">
                      {initialLoadComplete &&
                        categorizedUsers.shopManagers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[450px] text-center">
                          <Users className="w-20 h-20 text-gray-300 mb-2" />
                          <p className="text-gray-500 text-xl font-medium">
                            No Shop Managers available
                          </p>
                          <p className="text-gray-400 text-sm mt-2">
                            Shop managers will appear here when added
                          </p>
                        </div>
                      ) : (
                        <div className="h-[450px] overflow-y-auto pr-1">
                          <ul className="space-y-1">
                            {categorizedUsers.shopManagers.map((user) => (
                              <li key={user.id}>
                                <button
                                  className={`w-full text-left p-1 px-2 rounded border-2 transition-all duration-200
                                    ${selectedUser?.id === user.id
                                      ? 'border-primary bg-blue-50  ring-2 ring-blue-200'
                                      : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-md'
                                    }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${selectedUser?.id === user.id
                                          ? 'bg-primary text-white'
                                          : 'bg-gray-200 text-gray-700'
                                          }`}
                                      >
                                        {user.first_name.charAt(0)}
                                        {user.last_name.charAt(0)}
                                      </div>

                                      <span
                                        className={`font-semibold text-lg ${selectedUser?.id === user.id
                                          ? 'text-blue-700'
                                          : 'text-gray-900'
                                          }`}
                                      >
                                        {user.first_name} {user.last_name}
                                      </span>
                                    </div>

                                    <button
                                      onClick={() => handleUserClick(user)}
                                      className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 
                                                rounded bg-blue-100 text-blue-700 border border-blue-300 
                                                hover:bg-blue-200 transition"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  </div>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'regularUsers' && (
                    <div className="animate-in fade-in duration-300 h-[450px]">
                      {initialLoadComplete &&
                        categorizedUsers.regularUsers.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-[450px] text-center">
                          <Users className="w-20 h-20 text-gray-300 mb-2" />
                          <p className="text-gray-500 text-xl font-medium">
                            No Users available
                          </p>
                          <p className="text-gray-400 text-sm mt-2">
                            Regular users will appear here when added
                          </p>
                        </div>
                      ) : (
                        <div className="h-[450px] overflow-y-auto pr-1">
                          <ul className="space-y-1">
                            {categorizedUsers.regularUsers.map((user) => (
                              <li key={user.id}>
                                <button
                                  className={`w-full text-left p-1 px-2 rounded border-2 transition-all duration-200
                                      ${selectedUser?.id === user.id
                                      ? 'border-primary bg-blue-50  ring-2 ring-blue-200'
                                      : 'border-gray-300 bg-white hover:border-blue-400 hover:shadow-md'
                                    }`}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <div
                                        className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${selectedUser?.id === user.id
                                          ? 'bg-primary text-white'
                                          : 'bg-gray-200 text-gray-700'
                                          }`}
                                      >
                                        {user.first_name.charAt(0)}
                                        {user.last_name.charAt(0)}
                                      </div>
                                      <span
                                        className={`font-semibold text-lg ${selectedUser?.id === user.id
                                          ? 'text-blue-700'
                                          : 'text-gray-900'
                                          }`}
                                      >
                                        {user.first_name} {user.last_name}
                                      </span>
                                    </div>
                                    <button
                                      onClick={() => handleUserClick(user)}
                                      className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 
                                                rounded bg-blue-100 text-blue-700 border border-blue-300 
                                                hover:bg-blue-200 transition"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  </div>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {showEditModal && selectedUser && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
            onClick={handleCancel}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100 animate-in fade-in zoom-in">
              <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center rounded-t">
                <h2 className="text-xl font-semibold text-gray-900">
                  Edit User
                </h2>
                <button
                  title="Close button"
                  onClick={handleCancel}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <EditUserForm
                  selectedUser={selectedUser}
                  handleInputChange={handleInputChange}
                  handleSelectChange={handleSelectChange}
                  handleCheckboxChange={handleCheckboxChange}
                  handleSave={handleSave}
                  handleCancel={handleCancel}
                  emailError={emailError}
                  loadingModal={loadingModal}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {showNewUserModal && newUser && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-300"
            onClick={handleCancel}
          ></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-300 scale-100 animate-in fade-in zoom-in">
              <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center rounded">
                <h2 className="text-xl font-semibold text-gray-900">
                  Add New User
                </h2>
                <button
                  title="Close button"
                  onClick={handleCancel}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6">
                <NewUserForm
                  newUser={newUser}
                  handleInputChange={handleNewUserInputChange}
                  handleSelectChange={handleNewUserSelectChange}
                  handleCheckboxChange={handleNewUserCheckboxChange}
                  handleAddUser={handleAddUser}
                  handleCancel={handleCancel}
                  emailError={newUserEmailError}
                  loadingModal={loadingModal}
                  selectedBranchName={selectedBranch?.name}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ManageUsers
